import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';

import { adminCurrenciesSelector, getAdminCurrencies } from 'src/store/adminCurrency';
import {
  exchangesMerchantSelector,
  exchangesParsersSelector,
  getExchangesGivenMerchant,
  getExchangesParsers,
  getExchangesReceivedMerchant,
  receivedMerchantsSelector,
} from 'src/store/exchanges';

import {
  adminExchangeErrorsSelector,
  createAdminExchange,
  getAdminEditExchange,
  isLoadingExchangeSelector,
  selectedExchangeSelector,
  setAdminExchangeErrors,
  setSelectedExchange,
  updateAdminExchange,
} from 'src/store/adminExchanges';

import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomSwitch,
  Expand,
  PageLoader,
  WhiteCard,
} from 'src/components';

import './exchangeDirectionsForm.scss';

export const ExchangeDirectionsForm = () => {
  const { t } = useTranslation('exchangeDirectionsForm');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const currencies = useSelector(adminCurrenciesSelector);
  const isLoading = useSelector(isLoadingExchangeSelector);
  const merchants = useSelector(exchangesMerchantSelector);
  const receivedMerchants = useSelector(receivedMerchantsSelector);
  const parsers = useSelector(exchangesParsersSelector);
  const selectedExchange = useSelector(selectedExchangeSelector);
  const formError = useSelector(adminExchangeErrorsSelector);

  const updatedCurrencies =
    currencies?.map((item) => ({ value: item?.id?.toString(), label: item?.name })) || [];

  const updatedMerchants =
    merchants?.map((item) => ({ value: item, label: t(`merchant:${item}`) })) || [];

  const updatedReceivedMerchants =
    receivedMerchants?.map((item) => ({ value: item, label: t(`merchant:${item}`) })) || [];

  const updatedParsers = parsers?.map((item) => ({ value: item, label: item })) || [];

  const isEditMode = pathname?.includes('edit');

  const [formData, setFormData] = useState({
    currency_given_id: '',
    currency_received_id: '',
    string_identifier: '',
    type_max_exchange: '',
    max_exchange: '',
    type_min_exchange: '',
    min_exchange: '',
    type_commission: '',
    commission: '',
    type_calculating_commission: '',
    rate_given_currency: '',
    rate_received_currency: '',
    payment_processing_merchant: '',
    incoming_processing_merchant: '',
    active: false,
    active_change_export: false,
    descriptions: [],
    parser: {
      parser: '',
      correction_type: '',
      correction_method: '',
      correction: '',
      couple: '',
      dynamic: 0,
    },
    fields: {
      details_of_the_currency_being_given: 0,
      name: 0,
      email: 0,
      country: 0,
      passphrase: 0,
      date: 0,
      skype: 0,
      messenger: 0,
      bank_account_number: 0,
      bank_name: 0,
      purpose_of_payment: 0,
      details_of_the_currency_received: 0,
      middle_name: 0,
      phone_number: 0,
      city: 0,
      url: 0,
      time: 0,
      viber: 0,
      discord: 0,
      ifsc_bank_code: 0,
      virtual_billing_address: 0,
      account_holders_full_name: 0,
      destination_tag: 0,
      surname: 0,
      passport_number: 0,
      address: 0,
      comment: 0,
      telegram: 0,
      whatsapp: 0,
      snapchat: 0,
      bank_branch: 0,
      account_inn: 0,
      iban: 0,
    },
  });
  const [selectedTab, setSelectedTab] = useState('1');

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditExchange(params?.id));
    }

    dispatch(getAdminCurrencies());
    dispatch(getExchangesGivenMerchant());
    dispatch(getExchangesReceivedMerchant());
    dispatch(getExchangesParsers());

    return () => {
      dispatch(setSelectedExchange({}));
      dispatch(setAdminExchangeErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedExchange }));
    }
  }, [isEditMode, selectedExchange]);

  useEffect(() => {
    const firstPageErrors = [
      'currency_given_id',
      'currency_received_id',
      'string_identifier',
      'rate_given_currency',
      'rate_received_currency',
      'incoming_processing_merchant',
      'payment_processing_merchant',
    ];

    const secondPageErrors = [
      'type_commission',
      'type_calculating_commission',
      'parser.parser',
      'parser.correction_type',
      'parser.correction_method',
      'parser.correction',
      'parser.couple',
    ];

    for (const firstPageError of firstPageErrors) {
      if (formError?.[firstPageError]) {
        setSelectedTab('1');
      }
    }

    for (const secondPageError of secondPageErrors) {
      if (formError?.[secondPageError]) {
        setSelectedTab('2');
      }
    }
  }, [formError]);

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeParserInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, parser: { ...prev.parser, [key]: event.target.value } }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`parser.${key}`];

      dispatch(setAdminExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeParserSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, parser: { ...prev.parser, [key]: event.value } }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`parser.${key}`];

      dispatch(setAdminExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeParserSwitch = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({
        ...prev,
        parser: { ...prev.parser, [key]: event === true ? 1 : 0 },
      }));
    },
    [],
  );

  const onChangeFieldSwitch = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({
        ...prev,
        fields: { ...prev.fields, [key]: event === true ? 1 : 0 },
      }));
    },
    [],
  );

  const onClickTab = useCallback(
    (tab) => () => {
      setSelectedTab(tab);
    },
    [],
  );

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(updateAdminExchange({ id: params?.id, payload: formData, navigate }));
        return;
      }

      await dispatch(createAdminExchange({ navigate, payload: formData }));
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const formContentStyles = classNames('exchange-directions-form__content', {
    'exchange-directions-form__content--flex': selectedTab === '4',
  });

  const typeMinMaxOptions = [
    { label: 'Для отдаваемой валюты', value: 'given' },
    { label: 'Для получаемой валюты', value: 'received' },
  ];

  const additionalOptions = [
    { label: 'Процент', value: 'percent' },
    { label: 'Сумма', value: 'sum' },
  ];

  const normalizeContentInlineStyle = useCallback(() => {
    if (selectedTab === '1') {
      return {
        maxWidth: '789px',
        gridTemplateColumns: '1fr 1fr',
      };
    }
  }, [selectedTab]);

  const statusOptions = [
    {
      value: true,
      label: t('label:active'),
    },
    {
      value: false,
      label: t('label:inactive'),
    },
  ];

  return (
    <div className="exchange-directions-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>
          {t(`exchangeDirection:${!isEditMode ? 'create' : 'edit'}`)}
          <div className="exchange-directions-form__tabs">
            <CustomButton
              title={t('adminApplications:exchanges')}
              variant={selectedTab === '1' ? 'filled' : 'outlined'}
              onClick={onClickTab('1')}
            />
            <CustomButton
              title={t('adminApplications:parser')}
              variant={selectedTab === '2' ? 'filled' : 'outlined'}
              onClick={onClickTab('2')}
            />
            <CustomButton
              title={t('adminApplications:fields')}
              variant={selectedTab === '3' ? 'filled' : 'outlined'}
              onClick={onClickTab('3')}
            />
            {/* <CustomButton
              title={t('adminApplications:description')}
              variant={selectedTab === '4' ? 'filled' : 'outlined'}
              onClick={onClickTab('4')}
            /> */}
          </div>
        </h3>
        <form onSubmit={onSubmit}>
          <div className={formContentStyles} style={normalizeContentInlineStyle()}>
            {selectedTab === '1' && (
              <>
                <CustomSelect
                  label={t('givingCurrency')}
                  options={updatedCurrencies}
                  value={formData.currency_given_id?.toString()}
                  errorMessage={formError?.currency_given_id}
                  onChange={onChangeSelect('currency_given_id')}
                />
                <CustomSelect
                  label={t('receivingCurrency')}
                  options={updatedCurrencies}
                  value={formData.currency_received_id?.toString()}
                  errorMessage={formError?.currency_received_id}
                  onChange={onChangeSelect('currency_received_id')}
                />

                <CustomSelect
                  label={t('typeMinSum')}
                  value={formData.type_min_exchange}
                  options={typeMinMaxOptions}
                  onChange={onChangeSelect('type_min_exchange')}
                />
                <CustomInput
                  label={t('minExchangeRate')}
                  value={formData?.min_exchange}
                  onChange={onChangeInput('min_exchange')}
                  type="number"
                />
                <CustomSelect
                  label={t('typeMaxSum')}
                  value={formData.type_max_exchange}
                  options={typeMinMaxOptions}
                  onChange={onChangeSelect('type_max_exchange')}
                />
                <CustomInput
                  label={t('maxExchangeRate')}
                  value={formData?.max_exchange}
                  onChange={onChangeInput('max_exchange')}
                  type="number"
                />
                <CustomSelect
                  options={updatedMerchants}
                  label={t('merchantIncomePayments')}
                  value={formData?.payment_processing_merchant}
                  errorMessage={formError?.payment_processing_merchant}
                  onChange={onChangeSelect('payment_processing_merchant')}
                />
                <CustomSelect
                  options={updatedReceivedMerchants}
                  label={t('merchantOutcomePayments')}
                  value={formData?.incoming_processing_merchant}
                  errorMessage={formError?.incoming_processing_merchant}
                  onChange={onChangeSelect('incoming_processing_merchant')}
                />
                <CustomInput
                  label={t('givingExchangeRate')}
                  value={formData?.rate_given_currency}
                  errorMessage={formError?.rate_given_currency}
                  onChange={onChangeInput('rate_given_currency')}
                  type="number"
                />
                <CustomInput
                  label={t('receivingExchangeRate')}
                  value={formData?.rate_received_currency}
                  errorMessage={formError?.rate_received_currency}
                  onChange={onChangeInput('rate_received_currency')}
                  type="number"
                />
                <CustomSelect
                  label={t('verificationType')}
                  // options={statusOptions}
                  onChange={() => {}}
                  disabled
                />
                <CustomSelect
                  label={t('status')}
                  value={formData?.active}
                  options={statusOptions}
                  errorMessage={formError?.active}
                  onChange={onChangeSelect('active')}
                />
                <CustomSelect
                  label={t('passBestChange')}
                  value={formData?.active_change_export}
                  options={statusOptions}
                  errorMessage={formError?.active_change_export}
                  onChange={onChangeSelect('active_change_export')}
                />

                {/* <CustomSelect
                  label={t('typeComission')}
                  value={formData?.type_commission}
                  errorMessage={formError?.type_commission}
                  options={typeMinMaxOptions}
                  onChange={onChangeSelect('type_commission')}
                />
                <CustomSelect
                  label={t('wayAdditionalComission')}
                  value={formData?.type_calculating_commission}
                  errorMessage={formError?.type_calculating_commission}
                  options={additionalOptions}
                  onChange={onChangeSelect('type_calculating_commission')}
                />
                <CustomInput
                  label={t('stringIdentifier')}
                  value={formData?.string_identifier}
                  errorMessage={formError?.string_identifier}
                  onChange={onChangeInput('string_identifier')}
                />

                <CustomInput
                  label={t('sumAdditionalComission')}
                  value={formData.commission}
                  onChange={onChangeInput('commission')}
                  type="number"
                /> */}
              </>
            )}

            {selectedTab === '2' && (
              <>
                <CustomSelect
                  label={t('typeComission')}
                  value={formData?.type_commission}
                  errorMessage={formError?.type_commission}
                  options={typeMinMaxOptions}
                  onChange={onChangeSelect('type_commission')}
                />
                <CustomSelect
                  label={t('wayAdditionalComission')}
                  value={formData?.type_calculating_commission}
                  errorMessage={formError?.type_calculating_commission}
                  options={additionalOptions}
                  onChange={onChangeSelect('type_calculating_commission')}
                />
                <CustomInput
                  label={t('sumAdditionalComission')}
                  value={formData.commission}
                  onChange={onChangeInput('commission')}
                  type="number"
                />
                <CustomSelect
                  label={t('courseParser')}
                  value={formData.parser.parser}
                  options={updatedParsers}
                  errorMessage={formError?.['parser.parser']}
                  onChange={onChangeParserSelect('parser')}
                />
                <CustomSelect
                  label={t('calculatingCorrection')}
                  value={formData.parser.correction_method}
                  options={additionalOptions}
                  errorMessage={formError?.['parser.correction_method']}
                  onChange={onChangeParserSelect('correction_method')}
                />
                <CustomSelect
                  label={t('correctionType')}
                  value={formData.parser.correction_type}
                  options={typeMinMaxOptions}
                  errorMessage={formError?.['parser.correction_type']}
                  onChange={onChangeParserSelect('correction_type')}
                />
                <CustomInput
                  label={t('couple')}
                  value={formData.parser.couple}
                  errorMessage={formError?.['parser.couple']}
                  onChange={onChangeParserInput('couple')}
                />
                <CustomInput
                  label={t('correctionSum')}
                  value={formData.parser.correction}
                  errorMessage={formError?.['parser.correction']}
                  onChange={onChangeParserInput('correction')}
                  type="number"
                />
                <div className="exchange-directions-form__switch">
                  <CustomSwitch
                    label={t('dynamicExchangeRate')}
                    isActive={formData.parser.dynamic}
                    onChange={onChangeParserSwitch('dynamic')}
                  />
                </div>
              </>
            )}

            {selectedTab === '3' && (
              <>
                <CustomSwitch
                  label={t('reqGiveCurrency')}
                  isActive={formData.fields.details_of_the_currency_being_given}
                  onChange={onChangeFieldSwitch('details_of_the_currency_being_given')}
                />
                <CustomSwitch
                  label={t('reqReceiveCurrency')}
                  isActive={formData.fields.details_of_the_currency_received}
                  onChange={onChangeFieldSwitch('details_of_the_currency_received')}
                />
                <CustomSwitch
                  label={t('tag')}
                  isActive={formData.fields.destination_tag}
                  onChange={onChangeFieldSwitch('destination_tag')}
                />
                <CustomSwitch
                  label={t('firstName')}
                  isActive={formData.fields.name}
                  onChange={onChangeFieldSwitch('name')}
                />
                <CustomSwitch
                  label={t('middleName')}
                  isActive={formData.fields.middle_name}
                  onChange={onChangeFieldSwitch('middle_name')}
                />
                <CustomSwitch
                  label={t('lastName')}
                  isActive={formData.fields.surname}
                  onChange={onChangeFieldSwitch('surname')}
                />
                <CustomSwitch
                  label={t('email')}
                  isActive={formData.fields.email}
                  onChange={onChangeFieldSwitch('email')}
                />
                <CustomSwitch
                  label={t('phoneNumber')}
                  isActive={formData.fields.phone_number}
                  onChange={onChangeFieldSwitch('phone_number')}
                />
                <CustomSwitch
                  label={t('passportId')}
                  isActive={formData.fields.passport_number}
                  onChange={onChangeFieldSwitch('passport_number')}
                />
                <CustomSwitch
                  label={t('country')}
                  isActive={formData.fields.country}
                  onChange={onChangeFieldSwitch('country')}
                />
                <CustomSwitch
                  label={t('city')}
                  isActive={formData.fields.city}
                  onChange={onChangeFieldSwitch('city')}
                />
                <CustomSwitch
                  label={t('address')}
                  isActive={formData.fields.address}
                  onChange={onChangeFieldSwitch('address')}
                />
                <CustomSwitch
                  label={t('codeName')}
                  isActive={formData.fields.passphrase}
                  onChange={onChangeFieldSwitch('passphrase')}
                />
                <CustomSwitch
                  label={t('url')}
                  isActive={formData.fields.url}
                  onChange={onChangeFieldSwitch('url')}
                />
                <CustomSwitch
                  label={t('comment')}
                  isActive={formData.fields.comment}
                  onChange={onChangeFieldSwitch('comment')}
                />
                <CustomSwitch
                  label={t('date')}
                  isActive={formData.fields.date}
                  onChange={onChangeFieldSwitch('date')}
                />
                <CustomSwitch
                  label={t('time')}
                  isActive={formData.fields.time}
                  onChange={onChangeFieldSwitch('time')}
                />
                <CustomSwitch
                  label={t('telegram')}
                  isActive={formData.fields.telegram}
                  onChange={onChangeFieldSwitch('telegram')}
                />
                <CustomSwitch
                  label={t('skype')}
                  isActive={formData.fields.skype}
                  onChange={onChangeFieldSwitch('skype')}
                />
                <CustomSwitch
                  label={t('viber')}
                  isActive={formData.fields.viber}
                  onChange={onChangeFieldSwitch('viber')}
                />
                <CustomSwitch
                  label={t('whatsapp')}
                  isActive={formData.fields.whatsapp}
                  onChange={onChangeFieldSwitch('whatsapp')}
                />
                <CustomSwitch
                  label={t('messenger')}
                  isActive={formData.fields.messenger}
                  onChange={onChangeFieldSwitch('messenger')}
                />
                <CustomSwitch
                  label={t('discord')}
                  isActive={formData.fields.discord}
                  onChange={onChangeFieldSwitch('discord')}
                />
                <CustomSwitch
                  label={t('snapchat')}
                  isActive={formData.fields.snapchat}
                  onChange={onChangeFieldSwitch('snapchat')}
                />
                <CustomSwitch
                  label={t('bankId')}
                  isActive={formData.fields.bank_account_number}
                  onChange={onChangeFieldSwitch('bank_account_number')}
                />
                <CustomSwitch
                  label={t('ifscCode')}
                  isActive={formData.fields.ifsc_bank_code}
                  onChange={onChangeFieldSwitch('ifsc_bank_code')}
                />
                <CustomSwitch
                  label={t('bankNumber')}
                  isActive={formData.fields.bunk_branch}
                  onChange={onChangeFieldSwitch('bunk_branch')}
                />
                <CustomSwitch
                  label={t('bankName')}
                  isActive={formData.fields.bank_name}
                  onChange={onChangeFieldSwitch('bank_name')}
                />
                <CustomSwitch
                  label={t('virtualPaymentAddress')}
                  isActive={formData.fields.virtual_billing_address}
                  onChange={onChangeFieldSwitch('virtual_billing_address')}
                />
                <CustomSwitch
                  label={t('innEdrpou')}
                  isActive={formData.fields.account_inn}
                  onChange={onChangeFieldSwitch('account_inn')}
                />
                <CustomSwitch
                  label={t('paymentType')}
                  isActive={formData.fields.purpose_of_payment}
                  onChange={onChangeFieldSwitch('purpose_of_payment')}
                />
                <CustomSwitch
                  label={t('paymentHolderName')}
                  isActive={formData.fields.account_holders_full_name}
                  onChange={onChangeFieldSwitch('account_holders_full_name')}
                />
                <CustomSwitch
                  label={t('iban')}
                  isActive={formData.fields.iban}
                  onChange={onChangeFieldSwitch('iban')}
                />
              </>
            )}

            {selectedTab === '4' && (
              <>
                <Expand label={t('55')} isExpanded={true}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('56')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('57')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('58')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('59')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('60')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
                <Expand label={t('61')}>
                  <CustomInput label={t('content')} placeholder={t('content')} />
                </Expand>
              </>
            )}
          </div>
          <CustomButton variant="filled" title={t('title:save')} />
        </form>
      </WhiteCard>
    </div>
  );
};
